// eslint-disable-next-line no-unused-vars
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useAsyncDebounce } from 'react-table';

import { DataTableFilter } from '../DataTable/components/DataTableFilter';
import { DataTableContainer } from '../DataTable/components/DataTableContainer';
import { TableBody } from '../Table/components/TableBody';
import { TableRow } from '../Table/components/TableRow';
import { TableCell } from '../Table/components/TableCell';
import { TableEmptyState } from '../Table/components/TableEmptyState';
import { ScrollContainer } from '../ScrollContainer';

const ResultsCount = styled.div`
    width: 100%;
    background: ${({ theme }) => theme.colors.fixed.white};
    line-height: 2rem;
    height: 2rem;
    border: 1px solid rgba(67, 90, 111, 0.14);
    border-top: none;
    padding: 0 10px;
`;

export const SearchableList = ({
    id,
    items = [],
    renderRow,
    renderIcon,
    onItemClick,
    itemKey,
    filterFn,
    renderResultsCount,
    loading,
    listHeight,
    emptyStateLabel,
    ...props
}) => {
    const [searchFilter, setSearchFilter] = React.useState('');
    const onFilterChange = useAsyncDebounce((v) => setSearchFilter(v), 200);
    const filteredItems = React.useMemo(() => {
        return filterFn && searchFilter ? filterFn({ items, searchFilter }) : items;
    }, [searchFilter, items]);

    return (
        <div style={{ position: 'relative' }}>
            <DataTableFilter
                id={`${id}_filter`}
                placeholder={'Search...'}
                onChange={(value) => {
                    onFilterChange(value || undefined);
                }}
                clearable
            />

            <ScrollContainer height={listHeight}>
                <DataTableContainer
                    overflowX={'hidden'}
                    fullWidth
                    style={{ borderCollapse: 'separate', minHeight: '0' }}
                >
                    <TableBody loading={loading}>
                        {filteredItems.length ? (
                            filteredItems.map((item) => (
                                <TableRow onClick={() => onItemClick(item)} key={item[itemKey]}>
                                    <TableCell full>{renderRow(item)}</TableCell>
                                    <TableCell>{renderIcon(item)}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableEmptyState cols={0} label={searchFilter ? 'search results' : emptyStateLabel} />
                            </TableRow>
                        )}
                    </TableBody>
                </DataTableContainer>
            </ScrollContainer>
            {typeof renderResultsCount === 'function' ? (
                <ResultsCount>{renderResultsCount(items.length)}</ResultsCount>
            ) : null}
        </div>
    );
};

SearchableList.propTypes = {
    items: PropTypes.arrayOf(PropTypes.object),
    id: PropTypes.string,
    itemKey: PropTypes.string,
    emptyStateLabel: PropTypes.string,
    listHeight: PropTypes.string,
    renderRow: PropTypes.func,
    renderIcon: PropTypes.func,
    filterFn: PropTypes.func,
    renderResultsCount: PropTypes.func,
    onItemClick: PropTypes.func,
    loading: PropTypes.bool
};

SearchableList.defaultProps = {
    items: [],
    id: '',
    itemKey: '',
    emptyStateLabel: 'list items',
    renderRow: () => '',
    renderIcon: () => null,
    filterFn: () => [],
    onItemClick: () => '',
    loading: false
};
